import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import MoreMenu from '../components/ui/MoreMenu';
import { CommitteeRolesList } from '../components/CommitteeRoles';
import Pill from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice } from '../components/ui/States';
import { fmt, orDash } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import type {
  Contact,
  ContactErrorResponse,
  ContactKind,
  ContactListQuery,
  ContactPage,
  CreateContactRequest,
  DataResponse,
} from '../contracts';

type Archived = 'active' | 'archived' | 'all';

const KIND_LABEL: Record<string, string> = {
  supervisor: 'Supervisor',
  signatory: 'Signatory',
  admin_contact: 'Admin contact',
  speaker: 'Speaker',
  committee_external: 'External committee member',
  partner: 'Partner',
  outreach_target: 'Outreach',
  collaborator: 'Collaborator',
  knowledge_user: 'Knowledge user',
  lived_experience: 'Lived / living experience',
  event_attendee: 'Event attendee',
  competition: 'Competition applicant',
  department_head: 'Department head',
  training_director: 'Training director',
  advertising_network: 'Advertising network',
  ambassador: 'Ambassador',
  other: 'Other',
} satisfies Record<ContactKind, string>;
const KINDS = Object.keys(KIND_LABEL) as ContactKind[];

const inputClass = 'border border-line bg-white px-3 py-[9px] text-table w-full';

interface ApiError {
  response?: { status?: number; data?: ContactErrorResponse };
}

function errorMessage(err: unknown, fallback: string) {
  return (err as ApiError)?.response?.data?.error ?? fallback;
}

function ContactForm({
  contact,
  inUse,
  onClose,
  onSaved,
}: {
  contact: Contact | null;
  inUse: ContactKind[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [allKinds, setAllKinds] = useState(false);
  const [form, setForm] = useState({
    name: contact?.name ?? '',
    email: contact?.email ?? '',
    organization: contact?.organization ?? '',
    title: contact?.title ?? '',
    notes: contact?.notes ?? '',
    kinds: contact?.kinds ?? [],
    tags: (contact?.tags ?? []).join(', '),
  });
  const set = (k: keyof typeof form, v: string | string[]) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation(
    async () => {
      const body: CreateContactRequest = {
        ...form,
        kinds: form.kinds as ContactKind[],
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
      return contact ? api.patch(`/contacts/${contact.id}`, body) : api.post('/contacts', body);
    },
    { onSuccess: onSaved },
  );
  const existing = (save.error as ApiError | null)?.response?.data?.contact;
  const shownKinds =
    allKinds || !inUse.length ? KINDS : KINDS.filter((k) => inUse.includes(k) || form.kinds.includes(k));

  return (
    <Modal
      title={contact ? 'Edit contact' : 'New contact'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!form.name.trim() || save.isLoading} onClick={() => save.mutate()}>
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Name</label>
          <input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Email</label>
          <input className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Organization</label>
          <input
            className={inputClass}
            value={form.organization}
            onChange={(e) => set('organization', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Title</label>
          <input className={inputClass} value={form.title} onChange={(e) => set('title', e.target.value)} />
        </div>
      </div>
      <div className="text-small text-ink-2 mt-4 mb-2">Kind</div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {shownKinds.map((k) => (
          <label key={k} className="flex items-center gap-2 text-table">
            <input
              type="checkbox"
              checked={form.kinds.includes(k)}
              onChange={(e) => set('kinds', e.target.checked ? [...form.kinds, k] : form.kinds.filter((x) => x !== k))}
            />
            {KIND_LABEL[k]}
          </label>
        ))}
        {shownKinds.length < KINDS.length ? (
          <button
            type="button"
            className="text-table text-accent underline underline-offset-2"
            onClick={() => setAllKinds(true)}
          >
            More kinds
          </button>
        ) : null}
      </div>
      <label className="block text-small text-ink-2 mt-4 mb-1">
        Tags (comma separated: discipline, province…; used by event outreach lists)
      </label>
      <input
        className={inputClass}
        value={form.tags}
        onChange={(e) => set('tags', e.target.value)}
        placeholder="psychology, on"
      />
      <label className="block text-small text-ink-2 mt-4 mb-1">Notes</label>
      <textarea
        className={`${inputClass} h-[72px]`}
        value={form.notes}
        onChange={(e) => set('notes', e.target.value)}
      />
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>
            {errorMessage(save.error, 'Could not save the contact.')}
            {existing ? `: ${existing.name}${existing.archivedAt ? ' (archived)' : ''}.` : null}
          </ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

export default function Contacts() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [archived, setArchived] = useState<Archived>('active');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [seats, setSeats] = useState<Contact | null>(null);

  const list = useQuery<ContactPage>(
    ['contacts', q, kind, tag, archived, page],
    async () =>
      (
        await api.get<DataResponse<ContactPage>>('/contacts', {
          params: {
            q: q || undefined,
            kind: (kind || undefined) as ContactKind | undefined,
            tag: tag || undefined,
            archived,
            page,
          } satisfies ContactListQuery,
        })
      ).data.data,
  );
  const refresh = () => queryClient.invalidateQueries('contacts');
  const toggleArchive = useMutation(
    async (c: Contact) => api.post(`/contacts/${c.id}/${c.archivedAt ? 'unarchive' : 'archive'}`),
    { onSuccess: refresh },
  );
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.limit)) : 1;
  const kindCounts = list.data?.kindCounts ?? {};
  const inUse = KINDS.filter((k) => kindCounts[k]);
  const filterKinds = KINDS.filter((k) => kindCounts[k] || k === kind);

  return (
    <>
      <PageHeader
        title="Contacts"
        sub="People DIVERT works with who aren’t members, such as event guests and speakers. A contact with a member’s email is linked to that member."
        aside={
          mutable ? (
            <Button variant="primary" className="whitespace-nowrap" onClick={() => setEditing('new')}>
              New contact
            </Button>
          ) : null
        }
      />
      <Panel>
        <PanelHeader title="All contacts" note={list.data ? fmt(list.data.total) : undefined}>
          <select
            className="border border-line-strong bg-white px-2 py-[6px] text-small"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Every kind</option>
            {filterKinds.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
                {kindCounts[k] ? ` (${fmt(kindCounts[k])})` : ''}
              </option>
            ))}
          </select>
          {list.data?.tags?.length ? (
            <select
              className="border border-line-strong bg-white px-2 py-[6px] text-small"
              value={tag}
              onChange={(e) => {
                setTag(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Every tag</option>
              {list.data.tags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          ) : null}
          <select
            className="border border-line-strong bg-white px-2 py-[6px] text-small"
            value={archived}
            onChange={(e) => {
              setArchived(e.target.value as Archived);
              setPage(1);
            }}
          >
            <option value="active">Current</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
        </PanelHeader>
        <PanelBody>
          <input
            className={`${inputClass} mb-3`}
            placeholder="Search by name, email or organization"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
          {toggleArchive.error ? (
            <div className="mb-3">
              <ErrorNotice>{errorMessage(toggleArchive.error, 'Could not update the contact.')}</ErrorNotice>
            </div>
          ) : null}
          {list.isLoading ? (
            <SkeletonRows rows={6} />
          ) : list.error ? (
            <ErrorNotice>Could not load contacts.</ErrorNotice>
          ) : !list.data?.contacts.length ? (
            <EmptyState title={q || kind || tag ? 'No match' : 'No contacts yet'}>
              {q || kind || tag
                ? 'Nobody matches the search.'
                : 'Add supervisors, speakers and partners with New contact.'}
            </EmptyState>
          ) : (
            <>
              <table className="w-full max-md:block">
                <thead className="max-md:hidden">
                  <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Organization</th>
                    <th className="py-2 pr-4">Kind</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody className="max-md:block">
                  {list.data.contacts.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-line text-table align-top max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-x-3 max-md:gap-y-1 max-md:py-3"
                    >
                      <td className="py-[9px] pr-4 max-md:p-0 max-md:self-center">
                        <div className="max-md:font-semibold">{c.name}</div>
                        {c.title ? <div className="text-small text-ink-2">{c.title}</div> : null}
                      </td>
                      <td
                        className={`py-[9px] pr-4 max-md:p-0 max-md:col-span-2 max-md:row-start-2 max-md:text-small max-md:text-ink-2 max-md:break-words ${
                          c.email || c.organization || c.member ? '' : 'max-md:hidden'
                        }`}
                      >
                        <div>
                          <span className={c.email ? '' : 'max-md:hidden'}>{orDash(c.email)}</span>
                          {c.organization ? (
                            <span className="md:hidden">
                              {c.email ? ' · ' : ''}
                              {c.organization}
                            </span>
                          ) : null}
                        </div>
                        {c.member ? (
                          <Link className="text-small underline" to={`/analytics/members/${c.member.id}`}>
                            Member record
                          </Link>
                        ) : null}
                      </td>
                      <td className="py-[9px] pr-4 text-ink-2 max-md:hidden">{orDash(c.organization)}</td>
                      <td
                        className={`py-[9px] pr-4 max-md:p-0 max-md:col-span-2 max-md:row-start-3 max-md:pt-1 ${
                          c.archivedAt || c.restricted || c.kinds.length || c.tags?.length ? '' : 'max-md:hidden'
                        }`}
                      >
                        <div className="flex flex-wrap gap-1">
                          {c.archivedAt ? <Pill tone="inactive">Archived</Pill> : null}
                          {c.restricted ? <Pill tone="inactive">Restricted</Pill> : null}
                          {c.kinds.map((k) => (
                            <Pill key={k} tone="planned">
                              {KIND_LABEL[k] ?? k}
                            </Pill>
                          ))}
                          {(c.tags ?? []).map((t) => (
                            <span key={t} className="text-small text-ink-2 border border-line px-[5px]">
                              {t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-[9px] pr-4 text-right whitespace-nowrap max-md:hidden">
                        {c.kinds.includes('committee_external') && !c.restricted ? (
                          <Button variant="ghost" size="sm" onClick={() => setSeats(c)}>
                            Committees
                          </Button>
                        ) : null}
                        {mutable ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={toggleArchive.isLoading}
                              onClick={() => toggleArchive.mutate(c)}
                            >
                              {c.archivedAt ? 'Restore' : 'Archive'}
                            </Button>
                          </>
                        ) : null}
                      </td>
                      <td className="hidden max-md:flex max-md:col-start-2 max-md:row-start-1 items-center gap-2">
                        {mutable ? (
                          <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                            Edit
                          </Button>
                        ) : null}
                        <MoreMenu
                          label={`More for ${c.name}`}
                          items={[
                            ...(c.kinds.includes('committee_external') && !c.restricted
                              ? [{ label: 'Committees', onClick: () => setSeats(c) }]
                              : []),
                            ...(mutable
                              ? [
                                  {
                                    label: c.archivedAt ? 'Restore' : 'Archive',
                                    disabled: toggleArchive.isLoading,
                                    onClick: () => toggleArchive.mutate(c),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pages > 1 ? (
                <div className="flex items-center gap-3 mt-4 text-small text-ink-2">
                  <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </Button>
                  Page {page} of {pages}
                  <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                    Next
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </PanelBody>
      </Panel>
      {seats ? (
        <Modal title={`${seats.name}: committees`} onClose={() => setSeats(null)}>
          <CommitteeRolesList contactId={seats.id} />
        </Modal>
      ) : null}
      {editing ? (
        <ContactForm
          contact={editing === 'new' ? null : editing}
          inUse={inUse}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}
